'use client';

import { useEffect, useRef, useState } from 'react';

import ReactDOM from 'react-dom';

import EmojiPicker, { type EmojiClickData, Theme } from 'emoji-picker-react';
import { Smile } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { useBiLoggerContext } from '@/components/Contexts/BiLoggerContext';
import { BI_CLICK } from '@/constants/bi';

interface EmojiPickerButtonProps {
  onEmojiSelect: (emoji: string) => void;
}

const PICKER_WIDTH = 334;

export function EmojiPickerButton({ onEmojiSelect }: EmojiPickerButtonProps) {
  const t = useTranslations();
  const { reportClick } = useBiLoggerContext();
  const [open, setOpen] = useState(false);
  const [pickerStyle, setPickerStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  function handleToggle(e: React.MouseEvent<HTMLButtonElement>) {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const left = Math.max(
      8,
      Math.min(rect.left, window.innerWidth - PICKER_WIDTH - 8),
    );
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openAbove = spaceAbove > spaceBelow;
    setPickerStyle(
      openAbove
        ? {
            position: 'fixed',
            bottom: window.innerHeight - rect.top + 4,
            left,
            zIndex: 9999,
          }
        : { position: 'fixed', top: rect.bottom + 4, left, zIndex: 9999 },
    );
    setOpen(true);
  }

  function handleEmojiClick(emojiData: EmojiClickData) {
    reportClick({ clickType: BI_CLICK.EMOJI_SELECT, text: emojiData.emoji });
    onEmojiSelect(emojiData.emoji);
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: PointerEvent) {
      // Check by coordinates — works regardless of shadow DOM / web components
      const picker = pickerRef.current;
      const button = buttonRef.current;
      if (picker) {
        const r = picker.getBoundingClientRect();
        if (
          e.clientX >= r.left &&
          e.clientX <= r.right &&
          e.clientY >= r.top &&
          e.clientY <= r.bottom
        ) {
          return;
        }
      }
      if (button) {
        const r = button.getBoundingClientRect();
        if (
          e.clientX >= r.left &&
          e.clientX <= r.right &&
          e.clientY >= r.top &&
          e.clientY <= r.bottom
        ) {
          return;
        }
      }
      setOpen(false);
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        aria-label={t('get-feedback-emoji-pickEmoji')}
        className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      >
        <Smile className="h-3.5 w-3.5" />
      </button>
      {open &&
        ReactDOM.createPortal(
          <div ref={pickerRef} style={pickerStyle} data-emoji-picker-portal>
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              lazyLoadEmojis
              theme={Theme.LIGHT}
              width={PICKER_WIDTH}
              height={440}
              searchPlaceholder={t('get-feedback-emoji-searchPlaceholder')}
              style={
                {
                  '--epr-bg-color': '#ffffff',
                  '--epr-hover-bg-color': 'oklch(0.97 0 0)',
                  '--epr-focus-bg-color': 'oklch(0.97 0 0)',
                  '--epr-category-icon-active-color': 'rgb(90, 72, 245)',
                  '--epr-highlight-color': 'rgb(90, 72, 245)',
                  '--epr-border-color': 'oklch(0.922 0 0)',
                  '--epr-header-padding': '8px 8px 0',
                  '--epr-category-padding': '0 8px',
                  '--epr-emoji-size': '24px',
                  '--epr-emoji-padding': '5px',
                  '--epr-emoji-gap': '4px',
                  '--epr-shadow': '0 4px 16px rgba(0,0,0,0.12)',
                  '--epr-picker-border-radius': '10px',
                  '--epr-preview-height': '44px',
                } as React.CSSProperties
              }
            />
          </div>,
          document.body,
        )}
    </>
  );
}
